import { InboxMessage } from 'modules/types/Inbox';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { NavigationAndActionSearch } from '../icons';
import { InboxMessageCard } from './components';
import {
  StyledInboxContainer,
  StyledInboxMessageDisplay,
  StyledMessageCardsContainer,
  StyledNoMessagesContainer,
  StyledNoSelectedMessageContainer,
  StyledSearchInput,
  StyledSearchInputContainer,
} from './styled';

export interface InboxProps {
  messages?: InboxMessage[];
  handleMessageOpen: (id: number) => void;
  handleMessageSearch: (search: string) => void;
}

export const Inbox: React.FC<InboxProps> = ({
  messages,
  handleMessageOpen,
  handleMessageSearch,
}) => {
  const { t } = useTranslation();

  const [selectedMessage, setSelectedMessage] = useState<InboxMessage>();
  const [messageSearch, setMessageSearch] = useState<string>();
  const [messageDisplayVisible, setMessageDisplayVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (messageDisplayVisible) {
      document.body.classList.add('no-scroll-mobile');
    } else {
      document.body.classList.remove('no-scroll-mobile');
    }
  }, [messageDisplayVisible]);

  useEffect(() => {
    if (messageSearch) {
      const inboxMessageSearchTimeout = setTimeout(() => {
        handleMessageSearch(messageSearch);
      }, 500);

      // Search only when user stops typing.
      return () => clearTimeout(inboxMessageSearchTimeout);
    }

    handleMessageSearch('');
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageSearch]);

  return (
    <StyledInboxContainer>
      <StyledMessageCardsContainer>
        <StyledSearchInputContainer>
          <StyledSearchInput
            icon={<NavigationAndActionSearch />}
            iconClickAction={() =>
              messageSearch && handleMessageSearch(messageSearch)
            }
            inputChangeAction={setMessageSearch}
            placeholderText={t('search.searchPlaceholder')}
          />
        </StyledSearchInputContainer>
        {messages && messages.length !== 0 ? (
          messages.map((message) => (
            <React.Fragment key={message.id}>
              <InboxMessageCard
                message={message}
                active={selectedMessage && message.id === selectedMessage.id}
                onClick={() => {
                  handleMessageOpen(message.id);
                  setSelectedMessage(message);
                  setMessageDisplayVisible(true);
                }}
              />
            </React.Fragment>
          ))
        ) : (
          <StyledNoMessagesContainer>
            {t('inbox.noMessages')}
          </StyledNoMessagesContainer>
        )}
      </StyledMessageCardsContainer>
      <CSSTransition
        in={messageDisplayVisible}
        timeout={400}
        classNames="inbox-message-display"
        onExited={() => setSelectedMessage(undefined)}
        unmountOnExit>
        <StyledInboxMessageDisplay
          message={selectedMessage}
          onClose={() => setMessageDisplayVisible(false)}
        />
      </CSSTransition>
      {!selectedMessage && (
        <StyledNoSelectedMessageContainer>
          {t('inbox.noMessageSelected')}
        </StyledNoSelectedMessageContainer>
      )}
    </StyledInboxContainer>
  );
};
